import { useEffect, useState } from "react";
import { Footer } from "@/components/landing/Footer";
import { Link, useParams, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { ChevronLeft, ArrowUpRight, ArrowRight, Check, Activity, LayoutGrid, BarChart3, Brain, ShieldCheck, Gauge, GraduationCap } from "lucide-react";
import { useSeo, JsonLd, SITE_URL, PERSON_ID } from "@/lib/seo";
import { getModule, modulesBySystem, systemName, systemTag, isFree } from "@/data/modules";
import { WaitlistModal } from "@/components/landing/WaitlistModal";

const IMAGE = `${SITE_URL}/tq-logo.png`;
const SYS_ICON = { engine: Activity, discovery: LayoutGrid, data: BarChart3, intel: Brain, verify: ShieldCheck, risk: Gauge, edu: GraduationCap };

export default function ModulePage() {
  const { slug } = useParams();
  const mod = getModule(slug);
  const [open, setOpen] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useSeo({
    title: mod ? `${mod.seoTitle} | TradrQuant` : "Terminal Module | TradrQuant",
    description: mod ? mod.metaDescription : "",
    path: `/terminal/${slug}`,
    type: "article",
  });

  if (!mod) return <Navigate to="/terminal" replace />;

  const Icon = SYS_ICON[mod.system] || Activity;
  const url = `${SITE_URL}/terminal/${mod.slug}`;
  const related = modulesBySystem(mod.system).filter((m) => m.slug !== mod.slug);

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "TechArticle",
      headline: mod.seoTitle,
      description: mod.metaDescription,
      articleSection: systemName(mod.system),
      inLanguage: "en",
      image: IMAGE,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      author: { "@type": "Person", "@id": PERSON_ID, name: "JC Rod", alternateName: "Tradernaut", url: `${SITE_URL}/about` },
      publisher: { "@type": "Organization", name: "TradrQuant", logo: { "@type": "ImageObject", url: IMAGE } },
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: `TradrQuant — ${mod.name}`,
      applicationCategory: "FinanceApplication",
      operatingSystem: "Web",
      url,
      description: mod.metaDescription,
      author: { "@type": "Person", "@id": PERSON_ID, name: "JC Rod", alternateName: "Tradernaut" },
      isPartOf: { "@type": "SoftwareApplication", name: "TradrQuant Terminal", applicationCategory: "FinanceApplication", operatingSystem: "Web" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "The Terminal", item: `${SITE_URL}/terminal` },
        { "@type": "ListItem", position: 3, name: mod.name, item: url },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: mod.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
  ];

  return (
    <div data-testid="module-page" className="grain relative min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <JsonLd data={schema} />

      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl">
        <div className="mx-auto max-w-[900px] px-5 md:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <img src="/tq-logo.png?v=3" alt="TradrQuant" className="h-[22px] w-auto group-hover:scale-105 transition-transform duration-300" />
            <span data-testid="wordmark" className="tq-wordmark text-[#ECEBE4] text-[19px] sm:text-[21px]">Tradr<span className="tq-q">Q</span>uant</span>
          </Link>
          <Link to="/terminal" data-testid="module-back-btn" className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 border border-white/15 px-3 py-2 hover:text-[#BACD1C] hover:border-[#BACD1C]/50 transition-colors duration-300">
            <ChevronLeft size={14} /> all modules
          </Link>
        </div>
      </header>

      <article className="mx-auto max-w-[900px] px-5 md:px-10 pt-12 md:pt-16 pb-24">
        {/* hero */}
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-[#7D8390]">
          <Icon size={14} /> // {systemTag(mod.system)} · the terminal
        </div>
        <h1 className="mt-4 font-display font-semibold tracking-[-0.03em] text-4xl sm:text-5xl leading-[0.98]">
          {mod.name}
        </h1>
        <p className="mt-6 font-term text-[15px] text-white/70 leading-relaxed max-w-2xl">{mod.tagline}</p>

        <div className="mt-6 inline-flex items-center gap-2 border border-white/[0.10] bg-white/[0.03] px-3 py-1.5">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#BACD1C] opacity-70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#BACD1C]" />
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7D8390]">
            {isFree(mod.slug) ? "Free today · live now" : "In terminal · founder early release"}
          </span>
        </div>

        <div className="mt-7 flex flex-wrap gap-3">
          <button type="button" onClick={() => setOpen(true)} data-testid="module-waitlist-cta"
            className="inline-flex items-center gap-2 bg-[#BACD1C] text-black font-mono text-xs uppercase tracking-[0.14em] px-5 py-3 hover:bg-white transition-colors duration-300">
            Create free account <ArrowRight size={15} />
          </button>
          <Link to="/tools/tradrquant-pro-indicator" className="inline-flex items-center gap-2 border border-white/15 font-mono text-xs uppercase tracking-[0.14em] px-5 py-3 text-white/70 hover:text-[#BACD1C] hover:border-[#BACD1C]/40 transition-colors duration-300">
            Try the free indicator <ArrowUpRight size={14} />
          </Link>
        </div>

        {/* overview */}
        <div className="mt-14 space-y-4 font-term text-[15px] text-white/60 leading-relaxed">
          {mod.overview.map((p, i) => <p key={i}>{p}</p>)}
        </div>

        {/* capabilities */}
        <div className="mt-12">
          <div className="flex items-center gap-2.5 border-b border-white/10 pb-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#7D8390]">// capabilities</span>
            <span className="h-px flex-1 bg-gradient-to-r from-[#BACD1C]/30 to-transparent" />
          </div>
          <div className="mt-6 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {mod.capabilities.map(([t, d]) => (
              <div key={t} className="bg-[#0A0A0A] p-5 sm:p-6">
                <div className="inline-flex h-8 w-8 items-center justify-center border border-white/[0.10] bg-white/[0.03] text-[#BACD1C]"><Check size={15} /></div>
                <h3 className="mt-3.5 font-display font-semibold tracking-[-0.03em] text-[14px] leading-tight">{t}</h3>
                <p className="mt-2 font-mono text-[12px] text-white/50 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>

        {/* how it fits */}
        <div className="mt-12 border border-white/[0.10] bg-[#0D0E11] p-6 sm:p-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#7D8390]">// where it fits</div>
          <p className="mt-3 font-term text-[15px] text-white/70 leading-relaxed">{mod.fit}</p>
        </div>

        {/* related */}
        {related.length > 0 && (
          <div className="mt-12">
            <div className="flex items-center gap-2.5 border-b border-white/10 pb-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#7D8390]">// more in {systemName(mod.system)}</span>
              <span className="h-px flex-1 bg-gradient-to-r from-[#BACD1C]/30 to-transparent" />
            </div>
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              {related.map((r) => (
                <Link key={r.slug} to={`/terminal/${r.slug}`} data-testid={`related-${r.slug}`}
                  className="group flex items-center justify-between gap-3 border border-white/[0.08] bg-[#13151A] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)] hover:bg-[#1A1D24] hover:border-[#BACD1C]/30 px-4 py-3.5 transition-colors duration-300">
                  <span className="font-display font-semibold tracking-[-0.03em] text-[13px] text-white/85 group-hover:text-white">{r.name}</span>
                  <ArrowRight size={14} className="text-white/30 group-hover:text-[#BACD1C] group-hover:translate-x-0.5 transition-[color,transform] duration-300" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* faq */}
        <div className="mt-12">
          <div className="flex items-center gap-2.5 border-b border-white/10 pb-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#7D8390]">// faq</span>
            <span className="h-px flex-1 bg-gradient-to-r from-[#BACD1C]/30 to-transparent" />
          </div>
          <div className="mt-3 divide-y divide-white/10 border-b border-white/10">
            {mod.faqs.map((f) => (
              <div key={f.q} className="py-4">
                <h3 className="font-display font-semibold tracking-[-0.03em] text-[15px]">{f.q}</h3>
                <p className="mt-2 font-term text-[13px] text-white/55 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-14 border border-white/[0.10] bg-[#13151A] p-8 text-center">
          <h2 className="font-display font-semibold tracking-[-0.03em] text-2xl sm:text-3xl">Get early founder access</h2>
          <p className="mt-3 font-term text-sm text-white/55 max-w-lg mx-auto">The TradrQuant terminal is opening early to founders. Create a free account and get in as modules like {mod.name} roll out.</p>
          <button type="button" onClick={() => setOpen(true)} data-testid="module-waitlist-cta-bottom"
            className="mt-6 inline-flex items-center gap-2 bg-[#BACD1C] text-black font-mono text-xs uppercase tracking-[0.14em] px-6 py-3.5 hover:bg-white transition-colors duration-300">
            Create free account <ArrowRight size={15} />
          </button>
          <p className="mt-3 font-mono text-[10px] text-white/30 tracking-[0.15em] uppercase">Free account · no card</p>
        </div>

        <p className="mt-10 font-mono text-[11px] text-white/25 leading-relaxed">
          TradrQuant is an educational and analytical platform. Nothing here is financial advice. Trading involves substantial risk and past performance does not guarantee future results.
        </p>
      </article>

      <AnimatePresence>{open && <WaitlistModal open={open} onClose={() => setOpen(false)} />}</AnimatePresence>
      <Footer />
    </div>
  );
}
